import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { CHAT_MESSAGE_MAX_LENGTH, type ChatPersonDto } from '@taqon/contracts';
import { PersonAvatar } from '../../shared/ui/person';

/**
 * A message field where typing "@" offers the other participants of the chat to mention
 * (FRD-CORE-053): the composer and the edit dialog both use it. Picking a person writes
 * "@name " into the text and reports the person; mentions are sent as ids, never parsed from the
 * text (SDD §29.4). Only participants are offered — the API refuses anyone else.
 */
export function MentionTextField({
  id,
  people,
  value,
  onChange,
  onMention,
  onKeyDown,
  helperText,
  autoFocus = false,
}: {
  /** Unique on the page: names the list of people for assistive technology. */
  id: string;
  /** Who may be mentioned: the other participants. */
  people: readonly ChatPersonDto[];
  value: string;
  onChange: (value: string) => void;
  onMention: (person: ChatPersonDto) => void;
  /** Keys the list of people does not take (it takes the arrows, Enter, Tab and Escape while open). */
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  helperText?: ReactNode;
  autoFocus?: boolean;
}) {
  // The "@name" being typed at the caret, while the list of people is open.
  const [query, setQuery] = useState<{ start: number; end: number; text: string } | null>(null);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLTextAreaElement>(null);
  const suggestions =
    query === null
      ? []
      : people.filter((p) => p.displayName.toLowerCase().includes(query.text.toLowerCase()));
  const listId = `mention-list-${id}`;

  const findQuery = (text: string, caret: number) => {
    const typed = /(?:^|\s)@([^\s@]*)$/.exec(text.slice(0, caret))?.[1];
    setQuery(
      typed === undefined ? null : { start: caret - typed.length - 1, end: caret, text: typed },
    );
    setActive(0);
  };
  const pick = (person: ChatPersonDto) => {
    if (query === null) return;
    const inserted = `@${person.displayName} `;
    onChange(value.slice(0, query.start) + inserted + value.slice(query.end));
    onMention(person);
    setQuery(null);
    const caret = query.start + inserted.length;
    requestAnimationFrame(() => {
      input.current?.focus();
      input.current?.setSelectionRange(caret, caret);
    });
  };

  return (
    <Stack spacing={1.5}>
      <TextField
        label="Message"
        multiline
        minRows={2}
        maxRows={8}
        fullWidth
        autoFocus={autoFocus}
        value={value}
        inputRef={input}
        onChange={(event) => {
          onChange(event.target.value);
          findQuery(event.target.value, event.target.selectionStart ?? event.target.value.length);
        }}
        onKeyDown={(event) => {
          if (suggestions.length > 0) {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              const step = event.key === 'ArrowDown' ? 1 : -1;
              setActive((i) => (i + step + suggestions.length) % suggestions.length);
              return;
            }
            if (
              (event.key === 'Enter' && !event.ctrlKey && !event.metaKey) ||
              event.key === 'Tab'
            ) {
              event.preventDefault();
              const person = suggestions[active] ?? suggestions[0];
              if (person !== undefined) pick(person);
              return;
            }
            if (event.key === 'Escape') {
              // Closes the list only, not a dialog around the field.
              event.preventDefault();
              event.stopPropagation();
              setQuery(null);
              return;
            }
          }
          onKeyDown?.(event);
        }}
        onBlur={() => {
          // A suggestion keeps the focus on mouse down, so picking one does not get here first.
          setQuery(null);
        }}
        slotProps={{
          htmlInput: {
            maxLength: CHAT_MESSAGE_MAX_LENGTH,
            role: 'combobox',
            'aria-autocomplete': 'list',
            'aria-expanded': suggestions.length > 0,
            'aria-controls': suggestions.length > 0 ? listId : undefined,
            'aria-activedescendant':
              suggestions.length > 0 ? `${listId}-${String(active)}` : undefined,
          },
        }}
        helperText={helperText}
      />
      {suggestions.length > 0 && (
        <Paper variant="outlined">
          <List dense id={listId} role="listbox" aria-label="People to mention">
            {suggestions.map((person, index) => (
              <ListItemButton
                key={person.userId}
                id={`${listId}-${String(index)}`}
                role="option"
                aria-selected={index === active}
                selected={index === active}
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={() => {
                  pick(person);
                }}
              >
                <PersonAvatar name={person.displayName} userId={person.userId} size={20} />
                <ListItemText primary={`@${person.displayName}`} sx={{ ml: 1 }} />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}
    </Stack>
  );
}

/** The mentions to send: those picked whose "@name" is still in the text. */
export function keptMentions(
  picked: readonly ChatPersonDto[],
  text: string,
): readonly ChatPersonDto[] {
  return picked.filter((m) => text.includes(`@${m.displayName}`));
}
